// TelcoTrack — weld-layout v2 parse / normalize. Structural only — no cabinet policy.

import { ROZVADEC_SPLITTER_RATIOS, type RozvadecSplitterRatio } from './rozvadec-cabinet.constants';
import {
  canonicalRozvadecCassetteTypeId,
  type RozvadecCassetteTypeId,
} from './rozvadec-cassette.constants';
import {
  resolveWeldCassetteId,
  resolveWeldLinkId,
  resolveWeldTerminationId,
  sortWeldLayout,
  trimId,
} from './rozvadec-weld-layout.ids';
import {
  ROZVADEC_WELD_LAYOUT_VERSION,
  isRozvadecWeldLinkKind,
  isRozvadecWeldTerminationKind,
  type RozvadecWeldCassette,
  type RozvadecWeldIncomingRef,
  type RozvadecWeldLayout,
  type RozvadecWeldLayoutContext,
  type RozvadecWeldLayoutNormalizeResult,
  type RozvadecWeldLayoutOffers,
  type RozvadecWeldLink,
  type RozvadecWeldLinkKind,
  type RozvadecWeldTargetRef,
  type RozvadecWeldTermination,
  type RozvadecWeldTerminationKind,
} from './rozvadec-weld-layout.types';
import {
  disconnectNonOsCassetteBypassDirects,
  enforceRozvadecWeldLayoutOffers,
  sanitizeClientWeldLayoutShape,
} from './rozvadec-weld-layout.derive';
import { isCompleteWeldIncomingRef, isCompleteWeldTargetRef } from './rozvadec-weld-layout.ids';
import { expandPersistedOccupancyWelds } from './rozvadec-weld-billing';
import {
  occupancyEndpointId,
  parseOccupancyEndpointId,
  type OccupancyFiberRange,
  type OccupancyPatchConnection,
  type OccupancySplitterPath,
  type OccupancyWeldConnection,
} from './rozvadec-occupancy-connection';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** Bracket access — Angular compiles this package with `noPropertyAccessFromIndexSignature`. */
function field(record: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asInteger(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    if (Number.isInteger(parsed)) return parsed;
  }
  return fallback;
}

function asIntegerOrNull(value: unknown): number | null {
  const parsed = asInteger(value, Number.NaN);
  return Number.isInteger(parsed) ? parsed : null;
}

function recordHasUnitMapping(value: unknown): boolean {
  if (!isRecord(value)) return false;
  const label = field(value, 'unitLabel');
  const unitId = field(value, 'unitId');
  const labelPresent = typeof label === 'string' ? label.trim().length > 0 : label != null;
  const unitPresent = typeof unitId === 'string' ? unitId.trim().length > 0 : unitId != null;
  return labelPresent || unitPresent;
}

function scanUnitMapping(raw: unknown): boolean {
  if (!isRecord(raw)) return false;
  if (recordHasUnitMapping(raw)) return true;
  const collections = [field(raw, 'terminations'), field(raw, 'links'), field(raw, 'connections')];
  for (const collection of collections) {
    if (!Array.isArray(collection)) continue;
    if (collection.some((item) => recordHasUnitMapping(item))) return true;
  }
  return false;
}

function normalizeIncoming(raw: unknown, fallback: unknown): RozvadecWeldIncomingRef | null {
  const record = isRecord(raw)
    ? raw
    : isRecord(fallback)
      ? fallback
      : null;
  if (!record) return null;
  const cableId = trimId(field(record, 'cableId', 'inCableId'));
  const physicalFiber = asInteger(field(record, 'physicalFiber', 'inPhysicalFiber', 'inFiberNumber'), 0);
  if (!cableId || physicalFiber < 1) return null;
  return { cableId, physicalFiber };
}

function normalizeTarget(raw: unknown): RozvadecWeldTargetRef {
  const record = isRecord(raw) ? raw : {};
  return {
    buildingId: trimId(field(record, 'buildingId')),
    cableId: trimId(field(record, 'cableId', 'outCableId', 'targetCableId')),
    physicalFiber: asInteger(
      field(record, 'physicalFiber', 'outPhysicalFiber', 'outFiberNumber', 'targetFiber'),
      0,
    ),
  };
}

function normalizeLinkTarget(
  raw: Record<string, unknown>,
  cassetteId: string | null,
  peerIncoming: RozvadecWeldIncomingRef | null,
): RozvadecWeldTargetRef | null {
  const explicit = field(raw, 'target');
  if (explicit === null) return null;
  const target = normalizeTarget(explicit ?? (cassetteId || peerIncoming ? {} : raw));
  if ((cassetteId || peerIncoming) && !isCompleteWeldTargetRef(target)) return null;
  return target;
}

function normalizeOffers(raw: unknown): RozvadecWeldLayoutOffers {
  const record = isRecord(raw) ? raw : {};
  return {
    directEnabled: asBoolean(field(record, 'directEnabled'), false),
    connectorsEnabled: asBoolean(field(record, 'connectorsEnabled'), false),
  };
}

function normalizeRatio(kind: RozvadecWeldTerminationKind, raw: unknown): RozvadecSplitterRatio | null {
  if (kind !== 'splitter') return null;
  const value = typeof raw === 'string' ? raw.trim() : '';
  if ((ROZVADEC_SPLITTER_RATIOS as readonly string[]).includes(value)) {
    return value as RozvadecSplitterRatio;
  }
  return value ? (value as RozvadecSplitterRatio) : null;
}

function normalizeCassette(raw: unknown): RozvadecWeldCassette | null {
  if (!isRecord(raw)) return null;
  const drafted: RozvadecWeldCassette = {
    id: trimId(field(raw, 'id')),
    cassetteType: (canonicalRozvadecCassetteTypeId(trimId(field(raw, 'cassetteType', 'type'))) ??
      trimId(field(raw, 'cassetteType', 'type'))) as RozvadecCassetteTypeId,
    sectionId: trimId(field(raw, 'sectionId')),
    position: asInteger(field(raw, 'position'), -1),
  };
  return { ...drafted, id: resolveWeldCassetteId(drafted) };
}

function normalizeTermination(raw: unknown): RozvadecWeldTermination | null {
  if (!isRecord(raw)) return null;
  const kindValue = field(raw, 'kind');
  const kindRaw = typeof kindValue === 'string' ? kindValue.trim() : '';
  const kind: RozvadecWeldTerminationKind = isRozvadecWeldTerminationKind(kindRaw)
    ? kindRaw
    : (kindRaw as RozvadecWeldTerminationKind);
  const incoming = normalizeIncoming(field(raw, 'incoming'), raw);
  const cassetteId = trimId(field(raw, 'cassetteId')) || null;
  const drafted: RozvadecWeldTermination = {
    id: trimId(field(raw, 'id')),
    kind: kind || 'connector',
    cassetteId,
    slotInCassette: asIntegerOrNull(field(raw, 'slotInCassette', 'slot')),
    incoming,
    splitterRatio: normalizeRatio(kind || 'connector', field(raw, 'splitterRatio')),
  };
  if (!isRozvadecWeldTerminationKind(kind)) {
    drafted.kind = kindRaw as RozvadecWeldTerminationKind;
  }
  return { ...drafted, id: resolveWeldTerminationId(drafted) };
}

function normalizeLink(raw: unknown): RozvadecWeldLink | null {
  if (!isRecord(raw)) return null;
  const kindValue = field(raw, 'kind');
  const kindRaw = typeof kindValue === 'string' ? kindValue.trim() : '';
  const kind: RozvadecWeldLinkKind = isRozvadecWeldLinkKind(kindRaw)
    ? kindRaw
    : ((kindRaw || 'direct') as RozvadecWeldLinkKind);
  const incoming =
    kind === 'direct' || kind === 'splitter'
      ? normalizeIncoming(field(raw, 'incoming'), kind === 'direct' ? raw : null)
      : null;
  const peerIncoming =
    kind === 'direct' ? normalizeIncoming(field(raw, 'peerIncoming'), null) : null;
  const cassetteId = kind === 'direct' ? trimId(field(raw, 'cassetteId')) || null : null;
  const seatRaw = kind === 'direct' ? asIntegerOrNull(field(raw, 'seat')) : null;
  const terminationId = kind === 'direct' ? null : trimId(field(raw, 'terminationId')) || null;
  const splitterOutput =
    kind === 'splitter' ? asInteger(field(raw, 'splitterOutput', 'splitterOutputIndex'), 0) : null;
  const drafted: RozvadecWeldLink = {
    id: trimId(field(raw, 'id')),
    kind,
    incoming,
    peerIncoming: isCompleteWeldIncomingRef(peerIncoming) ? peerIncoming : null,
    cassetteId,
    ...(cassetteId && seatRaw != null && seatRaw >= 1 ? { seat: seatRaw } : {}),
    terminationId,
    splitterOutput: kind === 'splitter' ? splitterOutput : null,
    target: normalizeLinkTarget(raw, cassetteId, peerIncoming),
  };
  return { ...drafted, id: resolveWeldLinkId(drafted) };
}

function normalizeList<T>(raw: unknown, map: (item: unknown) => T | null): T[] {
  if (!Array.isArray(raw)) return [];
  const out: T[] = [];
  for (const item of raw) {
    const mapped = map(item);
    if (mapped) out.push(mapped);
  }
  return out;
}

function normalizeEndpointPair(raw: Record<string, unknown>): { id: string; from: string; to: string } | null {
  const from = trimId(field(raw, 'fromEndpointId'));
  const to = trimId(field(raw, 'toEndpointId'));
  if (!from || !to || !parseOccupancyEndpointId(from) || !parseOccupancyEndpointId(to)) return null;
  return { id: trimId(field(raw, 'id')) || `${from}→${to}`, from, to };
}

function assignStoredConnectorSide(endpointId: string, side: 'splice' | 'patch'): string {
  const parsed = parseOccupancyEndpointId(endpointId);
  if (parsed?.kind !== 'cassette-connector' || parsed.side) return endpointId;
  return occupancyEndpointId({ ...parsed, side });
}

function readFiberRange(raw: Record<string, unknown>): OccupancyFiberRange | undefined {
  const range = field(raw, 'fiberRange');
  if (!isRecord(range)) return undefined;
  const from = asInteger(field(range, 'from'), 0);
  const to = asInteger(field(range, 'to'), 0);
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < from) return undefined;
  return { from, to };
}

function readOptionalPositiveInt(raw: Record<string, unknown>, key: string): number | undefined {
  const value = asInteger(field(raw, key), 0);
  return Number.isInteger(value) && value >= 1 ? value : undefined;
}

function normalizeOccupancyWeld(raw: unknown): OccupancyWeldConnection | null {
  if (!isRecord(raw)) return null;
  const pair = normalizeEndpointPair(raw);
  if (!pair) return null;
  const fiberRange = readFiberRange(raw);
  const physicalWeldOperationCount = readOptionalPositiveInt(raw, 'physicalWeldOperationCount');
  const splicedFiberCount = readOptionalPositiveInt(raw, 'splicedFiberCount');
  return {
    id: pair.id,
    fromEndpointId: assignStoredConnectorSide(pair.from, 'splice'),
    toEndpointId: assignStoredConnectorSide(pair.to, 'splice'),
    ...(fiberRange ? { fiberRange } : {}),
    ...(physicalWeldOperationCount != null ? { physicalWeldOperationCount } : {}),
    ...(splicedFiberCount != null ? { splicedFiberCount } : {}),
  };
}

function normalizeOccupancyPatch(raw: unknown): OccupancyPatchConnection | null {
  if (!isRecord(raw)) return null;
  const pair = normalizeEndpointPair(raw);
  if (!pair) return null;
  return {
    id: pair.id,
    fromEndpointId: assignStoredConnectorSide(pair.from, 'patch'),
    toEndpointId: assignStoredConnectorSide(pair.to, 'patch'),
  };
}

function normalizeOccupancySplitterPath(raw: unknown): OccupancySplitterPath | null {
  if (!isRecord(raw)) return null;
  const cassetteId = trimId(field(raw, 'cassetteId'));
  const splitterIndex = asInteger(field(raw, 'splitterIndex'), 0);
  if (!cassetteId || !Number.isInteger(splitterIndex) || splitterIndex < 1) return null;
  const outputsRaw = field(raw, 'outputPatches');
  const outputPatches: OccupancySplitterPath['outputPatches'] = [];
  if (Array.isArray(outputsRaw)) {
    for (const item of outputsRaw) {
      if (!isRecord(item)) continue;
      const output = asInteger(field(item, 'output'), 0);
      if (!Number.isInteger(output) || output < 1) continue;
      const patchId = trimId(field(item, 'patchId')) || null;
      outputPatches.push({ output, patchId });
    }
  }
  return {
    id: trimId(field(raw, 'id')) || `spath:${cassetteId}:${splitterIndex}`,
    cassetteId,
    splitterIndex,
    inputPatchId: trimId(field(raw, 'inputPatchId')) || null,
    outputPatches,
  };
}

/**
 * Structural cleanup: trim, drop unit mapping, fill deterministic ids, sort.
 * Does not apply cabinet / cassette policy — use `canonicalizeRozvadecWeldLayout`.
 */
export function normalizeRozvadecWeldLayout(raw: unknown): RozvadecWeldLayoutNormalizeResult {
  const record = isRecord(raw) ? raw : {};
  const layout = sortWeldLayout({
    version: ROZVADEC_WELD_LAYOUT_VERSION,
    offers: normalizeOffers(field(record, 'offers')),
    cassettes: normalizeList(field(record, 'cassettes'), normalizeCassette),
    terminations: normalizeList(field(record, 'terminations'), normalizeTermination),
    links: normalizeList(field(record, 'links', 'connections'), normalizeLink),
    weldConnections: expandPersistedOccupancyWelds(
      normalizeList(field(record, 'weldConnections'), normalizeOccupancyWeld),
    ),
    patchConnections: normalizeList(field(record, 'patchConnections'), normalizeOccupancyPatch),
    splitterPaths: normalizeList(field(record, 'splitterPaths'), normalizeOccupancySplitterPath),
  });
  return { layout, hadUnitLabel: scanUnitMapping(raw) };
}

export function normalizeRozvadecWeldLayoutValue(raw: unknown): RozvadecWeldLayout {
  return normalizeRozvadecWeldLayout(raw).layout;
}

export function canonicalizeRozvadecWeldLayout(
  raw: unknown,
  context?: RozvadecWeldLayoutContext,
): RozvadecWeldLayout {
  const layout = normalizeRozvadecWeldLayoutValue(raw);
  if (!context) return layout;
  const withOffers = {
    ...layout,
    offers: enforceRozvadecWeldLayoutOffers(layout.offers, context.typeId),
  };
  return sanitizeClientWeldLayoutShape(
    disconnectNonOsCassetteBypassDirects(withOffers, context.typeId).layout,
    context.typeId,
  );
}

export function readRozvadecWeldLayoutOffers(raw: unknown): RozvadecWeldLayoutOffers | null {
  if (!isRecord(raw)) return null;
  if ('directEnabled' in raw || 'connectorsEnabled' in raw) {
    return normalizeOffers(raw);
  }
  const offers = field(raw, 'offers');
  if (isRecord(offers)) return normalizeOffers(offers);
  const spec = field(raw, 'spec');
  if (isRecord(spec) && isRecord(field(spec, 'offers'))) return normalizeOffers(field(spec, 'offers'));
  if (isRecord(spec) && ('directEnabled' in spec || 'connectorsEnabled' in spec)) {
    return normalizeOffers(spec);
  }
  return null;
}
